import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { LegacyOrderHook } from '../legacy-order-hook.js';
import { OrderSideEffectType } from './order-side-effect-type.js';
import { OrderSideEffect } from './order-side-effect.js';

export class GenerateProductQrSideEffect extends OrderSideEffect {
  static readonly TYPE_ID: string = 'e3b596f7dc374967af57e41f433fe6bb';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly effectType: OrderSideEffectType = OrderSideEffectType.GenerateProductQr;

  private constructor(
    public readonly qrPrefix: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return GenerateProductQrSideEffect.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GenerateProductQrSideEffect.SCHEMA_VERSION);
  }

  static create(args: {
    qrPrefix: string | null,
  }): GenerateProductQrSideEffect {
    return new GenerateProductQrSideEffect(args.qrPrefix);
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenerateProductQrSideEffect {
    return GenerateProductQrSideEffect.create({
      qrPrefix: MapUtils.getStringOrNull(map, 'qrPrefix'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'effectType': fromEnumAsString(this.effectType),
      'qrPrefix': this.qrPrefix,
    };
  }

  clone(): this {
    return GenerateProductQrSideEffect.create({
      qrPrefix: this.qrPrefix,
    }) as this;
  }

  isSame(other: GenerateProductQrSideEffect): boolean {
    if(this === other) {
      return true;
    }

    return other.effectType === this.effectType &&
      other.qrPrefix === this.qrPrefix;
  }

  toLegacyOrderHook(): LegacyOrderHook | null {
    return null;
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof GenerateProductQrSideEffect;
  }

  static assertType(order: DataModel): order is GenerateProductQrSideEffect {
    return order instanceof GenerateProductQrSideEffect;
  }
}

export class GenerateProductQrSideEffectBuilder extends DataModelBuilder<GenerateProductQrSideEffect> {
  build(context: SerializationContext, map: SerializedData): GenerateProductQrSideEffect {
    return GenerateProductQrSideEffect.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(GenerateProductQrSideEffect.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenerateProductQrSideEffect.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added [qrPrefix] of type [string | null]
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
